import { Avatar, AvatarFallback } from "@libs/zapi-ui/shadcn";
import { getUserFullName, getUserInitials } from "../lib/lib";
import { TCommentBase } from "../model/types/types";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import { formatRelativeTime } from "@/zapi-frontend/utils/dates";
import { CommentActions } from "./_CommentActions";
import { useState } from "react";
import { ReplyEditInputField } from "./_ReplyEditInputField";

type TReplyButtonProps = {
  commentReplies: TCommentBase[];
  isReplyOpen: boolean;
  userId: number;
  onDelete: (id: string) => void;
  parentId: string;
};

export function CommentReplies(props: TReplyButtonProps) {
  const { commentReplies, isReplyOpen, userId, parentId, onDelete } = props;

  const [isEditReply, setIsEditReply] = useState<string | null>(null);

  return (
    <div>
      {!isReplyOpen && (
        <div className="flex items-center gap-2 cursor-pointer">
          <div className="flex items-center">
            {commentReplies.slice(0, 2).map((reply, index) => (
              <Avatar key={reply.id} className={cn("size-5 ring-2 ring-white", index === 1 && "-ml-0.5")}>
                <AvatarFallback className="bg-innoveraZinc-150">
                  <FigmaTypography className="text-[10px]">{getUserInitials(reply.author)}</FigmaTypography>
                </AvatarFallback>
              </Avatar>
            ))}
            {commentReplies.length > 2 && (
              <Avatar className="size-5 bg-innoveraZinc-150 flex items-center justify-center -ml-0.5 ring-2 ring-white">
                <FigmaTypography variant="label-3">+{commentReplies.length - 2}</FigmaTypography>
              </Avatar>
            )}
          </div>
          <FigmaTypography variant="body-3" color="zinc500">
            {commentReplies.length} replies
          </FigmaTypography>
        </div>
      )}
      {isReplyOpen && (
        <div className="flex flex-col gap-3 mb-3">
          {commentReplies.map((reply, index) => {
            const isCanEdit = reply.author.id === userId;

            return (
              <div key={reply.id} className="rounded-[8px]" data-testid={`reply-comment-${reply.id}`}>
                <div className="flex justify-start gap-2 cursor-pointer">
                  <div className="flex flex-col items-center gap-1 overflow-hidden">
                    <Avatar className="size-5">
                      <AvatarFallback className="text-[10px]">{getUserInitials(reply.author)}</AvatarFallback>
                    </Avatar>
                    {index !== commentReplies.length - 1 && (
                      <div className="border-l-2 border-innoveraZinc-150 h-full mx-auto" />
                    )}
                  </div>
                  <div className="w-full flex gap-2 flex-col -mt-[6px]">
                    <div className={cn("flex gap-1 items-center mt-1")}>
                      <FigmaTypography variant="label-2" className="font-medium truncate" style={{ lineHeight: "120%" }}>
                        {getUserFullName(reply.author)}
                      </FigmaTypography>
                      <FigmaTypography variant="label-3" className="text-innoveraZinc-500 font-light">
                        {formatRelativeTime(reply.createdAt)}
                      </FigmaTypography>
                      <div className="flex items-center gap-1 ml-auto">
                        <CommentActions
                          commentId={reply.id}
                          onDelete={onDelete}
                          onEdit={() => setIsEditReply(reply.id)}
                          isCanEdit={isCanEdit}
                          isVisible={isReplyOpen}
                          isSmall
                        />
                      </div>
                    </div>

                    {isEditReply === reply.id && (
                      <ReplyEditInputField
                        userId={userId}
                        commentId={reply.id}
                        commentContent={reply.content}
                        isEditField
                        parentId={parentId}
                        onSubmit={() => setIsEditReply(null)}
                        onCancel={() => setIsEditReply(null)}
                        className="-ml-[26px]"
                      />
                    )}

                    {isEditReply !== reply.id && (
                      <FigmaTypography variant="body-2" className="ml-1 text-innoveraZinc-900">
                        {reply.content}
                      </FigmaTypography>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
